import { createStore } from "vuex";

// import cardClubs Module to use in createStore()
import cardClubsModule from "./cardClubs/cardClubStore";
import clubPlayersModule from "./clubPlayers/clubPlayersStore";
import initDatabaseModule from "./initDatabase/initDbStore";
import initDemoModule from "./initDatabase/initDemoUser";
import cardGamesModule from "./gamesData/gamesDataStore";
import authModule from "./auth/auth";
import scoreCardHeader from "./scoreCards/scoreCardHeaderStore";
import siteMembers from "./siteMember/siteMemberStore";
import { salesPrice } from "./appEnvironment";
import {
  completeLoadMeta,
  createIdleLoadMeta,
  failLoadMeta,
  startLoadMeta,
} from "../services/firebase/loadState";
import {
  clearFirestoreSessionCache,
  createScorecardRecord,
  loadHomeContext,
  openScorecard as openScorecardContext,
  updateLastScorecard,
} from "../services/firebase/firestoreDataLayer";

function applyHomeContext(commit, result, userId) {
  commit("cc/cardClubsLoadStarted", `card-clubs:${userId}`, { root: true });
  commit("cg/gamesDataLoadStarted", `games-data:${userId}`, { root: true });
  commit("sm/siteMemberLoadStarted", `site-member:${userId}`, { root: true });

  commit(
    "cc/loadCardClubs",
    result.data.cardClubs.length === 0
      ? false
      : { ccs: result.data.cardClubs, id: result.data.selectedCardClubId },
    { root: true }
  );
  commit("cc/cardClubsLoadSucceeded", result, { root: true });

  commit(
    "cg/loadGamesData",
    { cg: result.data.gamesData, cgID: result.data.selectedGameId },
    { root: true }
  );
  commit("cg/gamesDataLoadSucceeded", result, { root: true });

  commit("sm/loadSiteMember", result.data.siteMember, { root: true });
  commit("sm/siteMemberLoadSucceeded", result, { root: true });

  commit("sch/getListOfScoreCards", result.data.scoreCards, { root: true });
  commit("setHomeContextUserId", userId);
}

// Create references to other modules to use in
const store = createStore({
  modules: {
    cc: cardClubsModule,
    init: initDatabaseModule,
    initDemo: initDemoModule,
    cp: clubPlayersModule,
    cg: cardGamesModule,
    auth: authModule,
    sch: scoreCardHeader,
    sm: siteMembers,
    initDU: initDemoModule,
  },

  state: {
    /* Variables to show and hide global dialogs */
    snackbar: { show: false, text: "No Message" },
    aboutDialog: false,
    contactInfoDialog: false,
    tAndCDialog: false,

    /* Get subsription price from local.env file */
    salesPrice,

    //Proper number of players have been selected and now ready to play
    readyToCreateNewScoreCard: false,
    homeBootstrap: createIdleLoadMeta(),
    homeContextUserId: null,
    scorecardRoute: createIdleLoadMeta(),
  },

  getters: {
    aboutDialog(state) {
      return state.aboutDialog;
    },

    contactInfoDialog(state) {
      return state.contactInfoDialog;
    },
    tAndCDialog(state) {
      return state.tAndCDialog;
    },

    snackbarText(state) {
      return state.snackbar.text;
    },

    readyToCreateNewScoreCard(state) {
      return state.readyToCreateNewScoreCard;
    },

    salesPrice(state) {
      return state.salesPrice;
    },

    homeLoadState(state) {
      return state.homeBootstrap.loadState;
    },

    homeLoadError(state) {
      return state.homeBootstrap.error;
    },

    homeContextUserId(state) {
      return state.homeContextUserId;
    },

    scorecardRouteLoadState(state) {
      return state.scorecardRoute.loadState;
    },

    scorecardRouteLoadError(state) {
      return state.scorecardRoute.error;
    },
  },

  mutations: {
    //Turn on the snackbar
    showSnackBar(state, msg) {
      if (state.snackbar.show === false) {
        state.snackbar.text = msg;
        state.snackbar.show = true;
      } else {
        state.snackbar.show = false;
        setTimeout(() => {
          state.snackbar.text = msg;
          state.snackbar.show = true;
        }, 2000);
      }
    },
    hideSnackbar(state) {
      state.snackbar.show = false;
    },

    readyToCreateNewScoreCard(state, value) {
      state.readyToCreateNewScoreCard = value;
    },

    aboutDialogShow(state) {
      state.aboutDialog = true;
    },
    aboutDialogHide(state) {
      state.aboutDialog = false;
    },
    contactInfoDialogShow(state) {
      state.contactInfoDialog = true;
    },
    contactInfoDialogHide(state) {
      state.contactInfoDialog = false;
    },
    tAndCDialogShow(state) {
      state.tAndCDialog = true;
    },
    tAndCDialogHide(state) {
      state.tAndCDialog = false;
    },

    homeBootstrapStarted(state, requestKey) {
      state.homeBootstrap = startLoadMeta(requestKey);
    },

    homeBootstrapSucceeded(state, result) {
      state.homeBootstrap = completeLoadMeta(state.homeBootstrap, result);
    },

    homeBootstrapFailed(state, error) {
      state.homeBootstrap = failLoadMeta(state.homeBootstrap, error);
    },

    scorecardRouteStarted(state, requestKey) {
      state.scorecardRoute = startLoadMeta(requestKey);
    },

    scorecardRouteSucceeded(state, result) {
      state.scorecardRoute = completeLoadMeta(state.scorecardRoute, result);
    },

    scorecardRouteFailed(state, error) {
      state.scorecardRoute = failLoadMeta(state.scorecardRoute, error);
    },

    setHomeContextUserId(state, userId) {
      state.homeContextUserId = userId;
    },

    resetHomeBootstrapState(state) {
      state.homeBootstrap = createIdleLoadMeta();
      state.homeContextUserId = null;
    },

    resetScorecardRouteState(state) {
      state.scorecardRoute = createIdleLoadMeta();
    },
  },

  actions: {
    async initializeHome({ commit, rootGetters, state }, options = {}) {
      const userId = rootGetters["auth/loggedInSiteMemberID"];
      const force = options?.force === true;

      if (!userId) {
        commit("resetHomeBootstrapState");
        return {
          data: null,
          endedAt: Date.now(),
          error: null,
          source: "memory",
          startedAt: Date.now(),
        };
      }

      if (
        !force &&
        state.homeContextUserId === userId &&
        state.homeBootstrap.loadState === "ready"
      ) {
        return {
          data: null,
          endedAt: Date.now(),
          error: null,
          source: "memory",
          startedAt: Date.now(),
        };
      }

      commit("homeBootstrapStarted", `home:${userId ?? "anonymous"}`);

      const result = await loadHomeContext(userId);
      if (result.error) {
        commit("homeBootstrapFailed", result.error);
        commit("cc/cardClubsLoadFailed", result.error, { root: true });
        commit("cg/gamesDataLoadFailed", result.error, { root: true });
        commit("sm/siteMemberLoadFailed", result.error, { root: true });
        commit("sch/getListOfScoreCards", false, { root: true });
        return result;
      }

      applyHomeContext(commit, result, userId);
      commit("homeBootstrapSucceeded", result);
      return result;
    },

    async refreshHomeContext({ dispatch }) {
      clearFirestoreSessionCache();
      return dispatch("initializeHome", { force: true });
    },

    async openScorecard({ commit, rootGetters }, scorecardId) {
      commit("scorecardRouteStarted", `scorecard:${scorecardId}`);
      commit(
        "sch/scoreCardLoadStarted",
        { requestKey: `scorecard:${scorecardId}`, scoreCardId: scorecardId },
        { root: true }
      );
      commit("sch/scoreCardLoading", null, { root: true });

      const result = await openScorecardContext(
        scorecardId,
        rootGetters["cg/gamesData"]
      );

      if (result.error) {
        commit("scorecardRouteFailed", result.error);
        commit("sch/scoreCardLoadFailed", result.error, { root: true });
        return result;
      }

      commit("sch/createScoreCard", result.data.scoreCard, { root: true });
      commit("sch/loadGameData", result.data.gameData, { root: true });
      commit("sch/scoreCardLoadSucceeded", result, { root: true });
      commit("scorecardRouteSucceeded", result);
      return result;
    },

    async createScorecard({ commit, dispatch, rootGetters }, payload = {}) {
      const now = new Date();
      const scorecardPayload = {
        ccID: rootGetters["cc/cardClubID"],
        ccName: rootGetters["cc/cardClubName"],
        customizable: rootGetters["cg/gdsCustomizable"],
        dateCreated: now.toDateString(),
        gameEnds: rootGetters["cg/gameEnds"],
        gamePlayedBy: rootGetters["cg/gdsGamePlayedBy"],
        gdID: rootGetters["cg/gdsGameDataID"],
        gdName: rootGetters["cg/gdsName"],
        games: [],
        hands: [],
        lastDealer: 0,
        matches: [],
        players: rootGetters["cp/cpsAllNamesAndID"],
        scoreCardName:
          payload.scoreCardName ||
          `${rootGetters["cc/cardClubName"]} / ` +
            `${rootGetters["cg/gdsGameTranslatedName"]} / ` +
            `${now.toDateString()} / ${now.toLocaleTimeString()}`,
        siteMemberID: rootGetters["auth/loggedInSiteMemberID"],
        siteMemberName: rootGetters["auth/userDisplayName"],
        teams: rootGetters["cp/cpsTeamNames"],
        vueFileToLoad: rootGetters["cg/gdsVueFile"],
      };

      commit("sch/scoreCardOrigination", "new", { root: true });
      commit("scorecardRouteStarted", "scorecard:create");
      commit(
        "sch/scoreCardLoadStarted",
        { requestKey: "scorecard:create", scoreCardId: null },
        { root: true }
      );
      commit("sch/scoreCardLoading", null, { root: true });

      const createResult = await createScorecardRecord(scorecardPayload);
      if (createResult.error) {
        commit("scorecardRouteFailed", createResult.error);
        commit("sch/scoreCardLoadFailed", createResult.error, { root: true });
        return createResult;
      }

      await dispatch("cc/writeTeamNames", createResult.data.teams, { root: true });

      const openResult = await dispatch("openScorecard", createResult.data.id);
      if (openResult?.error) {
        return openResult;
      }

      dispatch(
        "persistLastOpenedScorecard",
        {
          userID: rootGetters["auth/loggedInSiteMemberID"],
          lastScoreCard: createResult.data.id,
        },
        { root: true }
      ).catch(() => {});

      dispatch("refreshHomeContext").catch(() => {});
      return openResult;
    },

    async retryHomeSection({ dispatch }) {
      return dispatch("refreshHomeContext");
    },

    async retryScorecard({ dispatch, rootGetters }) {
      const scorecardId = rootGetters["sch/lastRequestedScoreCardId"];
      if (!scorecardId) {
        return null;
      }
      return dispatch("openScorecard", scorecardId);
    },

    async persistLastOpenedScorecard(_, payload) {
      return updateLastScorecard(payload.userID, payload.lastScoreCard);
    },

    resetHomeContext({ commit }) {
      clearFirestoreSessionCache();
      commit("cc/resetCardClubsState", null, { root: true });
      commit("cg/resetGamesDataState", null, { root: true });
      commit("cp/resetClubPlayersState", null, { root: true });
      commit("sch/resetScoreCardListState", null, { root: true });
      commit("sm/resetSiteMemberState", null, { root: true });
      commit("resetHomeBootstrapState");
      commit("resetScorecardRouteState");
    },
  },
});

export default store;
